/**
 * formRecoveryStore — the restore point ladder loaded for the open form version,
 * which row the user selected, what the server said that row looked like, and
 * whether the page is previewing it (SYD-2450). Consumers: `FormRecoveryGroup`
 * (the sidebar list), `FormRecoveryConfirmModalContent`, `FormRecoveryBanner`
 * (preview and restored notices), `useFormRecoveryFieldState` (the per-field tint),
 * and the three hooks that own the callables — `useFormRecoveryList`,
 * `useFormRecoveryRead` and `useFormRecoveryRestore` — each of which writes to this
 * proxy at its own call site rather than through a wrapping action.
 *
 * `points` is one strictly-decreasing list for the whole panel: the ladder lands as
 * a whole, and a refinement of one gap splices its rows in directly after the later
 * row rather than nesting them. There is no page and no cursor to resume from.
 * Cursors are opaque server-minted tokens — `committedAtMillis` is the value to render.
 *
 * The form and its tables are one document to the operator, so there is one timeline:
 * `moments` merges `points` with one `tableLists` entry per table the layout holds,
 * collapsed to the whole minute. `points` and `tableLists` are kept as the sources the
 * merge was built from, so a refinement can splice into one and re-merge. The sidebar
 * renders `moments`; `points` is still what a refinement addresses, because a gap is a
 * pair of version cursors and only the version ladder can be walked between them.
 *
 * Selecting a moment reads every source it names, so the read state is a pair: `result`
 * for the version and one `tableReads` entry per table. They are kept apart because
 * they fail apart — the version's refusal is the moment's, a table's is that table's
 * alone — and because each table entry carries the digests a restore has to echo back.
 *
 * A successful restore calls `select(null)` rather than adding a restore-specific
 * mutator: live now matches what was read, so the selected row, its result and any
 * preview tint are exactly what selecting another row already discards.
 *
 * This module imports nothing outside `#types` and its sibling stores, and that is
 * load-bearing: it is reached through `store/form/index.ts` and `store/index.ts`,
 * so a callable import here would initialize Firebase Functions for every consumer
 * of the store barrel, tests included. The callables live in hooks for that reason.
 * `enterPreview` does reach an external system through
 * `pendingSavesActions.flushAll()`, which sends every debounced field write — that
 * is the point of it, and it costs no module-scope initialization.
 *
 * State lives here rather than in the group because the sidebar `Drawer` sets
 * `destroyOnHidden`: the group unmounts the moment the drawer closes or the
 * segmented view changes, and a preview has to outlive that. `formStore` clears
 * this store on `reset` and on every form/version change, so rows walked on form A
 * can never be shown against form B.
 */

import { proxy } from 'valtio';

import type {
    ArTableId,
    ArTableRestorePoint,
    ArTableRowDiffEntry,
    ArTableSkippedRow,
    FormPitrWindow,
    FormRestorePoint,
    FormRestorePointsEnd,
    ReadArTableAtResponse,
    ReadFormVersionAtResponse,
} from '#types';

import { pendingSavesActions } from '../pendingSavesStore';

/**
 * `idle`: the ladder has not been asked for since the last clear.
 * `loading`: a ladder request is in flight.
 * `loaded`: `points` holds the ladder, plus every gap refined since.
 * `failed`: the ladder was refused; `listError` is the server's words.
 */
export type FormRecoveryListStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/**
 * `idle`: no row has been read since the last selection changed.
 * `reading`: a call is in flight.
 * `read`: `result` describes the row in `selectedCursorMicros`.
 * `refused`: the server would not read that row; `refusal` is its words.
 */
export type FormRecoveryStatus = 'idle' | 'reading' | 'read' | 'refused';

/**
 * What one table of a landed restore came back with, projected off `RestoredArTable`
 * to the counts and handles the banner needs. The whole row diff is not kept: it can
 * run to hundreds of entries and `restoredFrom` outlives the route's other read state.
 */
export type FormRecoveryRestoredTable = {
    tableId: ArTableId;
    /**
     * Rows the transaction wrote, counted by what it did to each — the same three
     * actions the confirmation groups its summary by, so the notice afterwards can
     * speak of rows that changed, came back and went away in those words.
     */
    restored: Record<ArTableRowDiffEntry['action'], number>;
    /**
     * The rows the restore was told to leave behind, and why. `[]` on every ordinary
     * restore; non-empty only when the operator confirmed `Restore without the locked
     * rows`, and then the only record of which rows stand at the retained moment and
     * which still stand at the present — so a partial restore cannot report itself as
     * a clean one.
     */
    skipped: ArTableSkippedRow[];
    /**
     * The trash `operationId` this table's deleted rows were filed under, `null` when
     * it deleted none. The handle `arTableRowUndo` acts on; nothing in the form's own
     * undo stack reaches these rows.
     */
    trashOperationId: string | null;
};

/** The restore that just landed, as the state G banner names it. */
export type FormRecoveryRestoredFrom = {
    /** The restored moment, epoch millis — never a cursor. The time its sidebar row showed. */
    atMillis: number;
    /**
     * Keys the transaction wrote back on the version document itself. `0` on a
     * restore that changed only rows, which touches no version document by design —
     * so the banner counts rows beside this rather than from it, and such a restore
     * never reads as nothing having happened.
     */
    count: number;
    /** One entry per table the restore named, in request order. `[]` when it named none. */
    tables: FormRecoveryRestoredTable[];
    /**
     * The `formUndoStack` entry this restore recorded, or `null` when it recorded
     * none — a reference-only restore has nothing the stack can revert. The banner's
     * undo acts on this entry alone: it presses only while the id is still what
     * `formUndoStack.availability.undo.entryId` names, so a field typed after the
     * restore is never what the button reverts.
     *
     * It covers the version's fields and nothing else. A restore that also wrote rows
     * leaves those rows restored when this entry is reverted, which is why the banner
     * reads `tables` and words the button accordingly rather than offering one press
     * for two mechanisms.
     */
    undoEntryId: string | null;
};

/** One table's ladder, exactly as `listArTableRestorePoints` answered for it. */
export type RecoveryTableList = {
    tableId: ArTableId;
    /** Strictly decreasing `cursorMicros`; `points[0]` is live. */
    points: ArTableRestorePoint[];
    end: FormRestorePointsEnd;
    refusedMomentCount: number;
};

/**
 * A table whose ladder never landed. The timeline degrades rather than failing —
 * one table's refusal is not the form's history gone — so this is recorded, named
 * beside the list, and counted into the merged `refusedMomentCount`.
 */
export type RecoveryTableListError = {
    tableId: ArTableId;
    /** The server's own words. */
    message: string;
};

/** One table's contribution to a merged moment. */
export type RecoveryMomentTable = {
    tableId: ArTableId;
    /**
     * The rung this table reported inside the moment's minute. Its own
     * `cursorMicros` may be older than the moment's, which is the whole reason the
     * moment carries the newest cursor in the minute rather than this one.
     */
    point: ArTableRestorePoint;
};

/**
 * One row of the recovery timeline: a moment at which at least one source — the
 * form version, or one of its tables — reported a state of its own. Sources are
 * collapsed to the whole minute, which is the grain both ladders already share
 * beyond the exact-time margin; without that a form with two tables would draw
 * thirty-nine rows for thirteen moments.
 *
 * `cursorMicros` is the NEWEST cursor of the sources collapsed here, so reading at
 * it is a moment every one of them can answer for. It is the address the read and
 * the restore act on, for the version and for every table in `tables` alike.
 *
 * A source appearing here reported a rung at this minute, and every rung but a
 * ladder's live head differs from the rung above it — so `version` and `tables`
 * together are what changed at this moment. `version` is `null` when only tables
 * reported, which is the case the sidebar exists to stop hiding.
 */
export type RecoveryMoment = {
    cursorMicros: number;
    /** The same moment, epoch millis, for display. */
    atMillis: number;
    /** The moment's own cursor is beyond the exact-time margin, so it renders to whole minutes. */
    minuteGrain: boolean;
    /**
     * The record as it stands now: every source's live head, collapsed here. At most one
     * moment carries it, and it is the one moment a restore can do nothing with — nothing
     * changed at it, it IS the current state — so nothing may name it as what changed.
     */
    isLive: boolean;
    version: FormRestorePoint | null;
    /**
     * In layout order, at most one entry per table. `readonly` so a moment read off a
     * snapshot is still a `RecoveryMoment` and the row can take one as a prop.
     */
    tables: readonly RecoveryMomentTable[];
};

/**
 * `reading`: this table's call is in flight. `read`: `result` describes the table at
 * `atCursorMicros`. `refused`: the server would not read it there, and `refusal`
 * carries its words. There is no `idle`, because an entry exists only because a read
 * was issued for it.
 */
export type RecoveryTableReadStatus = 'reading' | 'read' | 'refused';

/**
 * One table's read of the selected moment, and — once it lands — the two digests a
 * restore of that table has to echo back.
 *
 * `atCursorMicros` is stamped when the entry is created and never afterwards, and it
 * sits in the same object as the digests. A response may only be written into an entry
 * whose stamp equals the cursor its own request named, so a digest cannot be filed
 * against a moment it was not read at, and nothing can pick a digest up without the
 * cursor it belongs to. That is what stops a restore writing a moment the operator
 * never confirmed.
 */
export type RecoveryTableRead = {
    tableId: ArTableId;
    atCursorMicros: number;
    status: RecoveryTableReadStatus;
    /** The server's whole answer, `null` until it lands and after a refusal. */
    result: ReadArTableAtResponse | null;
    /** The server's own words for a refused read, `null` otherwise. */
    refusal: string | null;
};

export type FormRecoveryState = {
    /**
     * Every restore point on screen, newest first and strictly decreasing by
     * `cursorMicros`. A ladder response replaces the list; a refinement splices its
     * rows in after the later row it was asked about. A row is immutable once
     * loaded: re-reading one costs a second billed read and the commit time it
     * carries cannot change.
     */
    points: FormRestorePoint[];
    /**
     * The tables the merge covered, by id in layout order — every table the form
     * version's layout holds, whether or not it is mounted. `useFormTableIds` is
     * where they come from and why the mounted registry is not.
     */
    tableIds: ArTableId[];
    /** One entry per table whose ladder landed, in the order of `tableIds`. */
    tableLists: RecoveryTableList[];
    /** One entry per table whose ladder did not. Empty on a clean listing. */
    tableListErrors: RecoveryTableListError[];
    /**
     * The timeline itself: `points` and every `tableLists` entry merged to the whole
     * minute, strictly decreasing by `cursorMicros`. This is what the sidebar renders
     * and what a selection addresses; `points` and `tableLists` are the sources it was
     * built from, kept so a refinement can splice into one of them and re-merge.
     */
    moments: RecoveryMoment[];
    listStatus: FormRecoveryListStatus;
    /**
     * Raised by `useFormRecoveryList` before every ladder request it sends, and the
     * only thing that tells one answer from another's: the requests are identical, so
     * nothing in them could. Monotonic, and never reset — a clear that restarted the
     * count would let a response in flight match a later request.
     *
     * A refinement captures it too, and for a second reason: its rows were measured
     * against the gaps of one ladder, so splicing them into a list a later ladder
     * built could put the list out of order and collide a row key.
     */
    listRequestId: number;
    /**
     * The form version `points` and `listStatus` describe, `null` before the first
     * request is sent. `load()` compares the open version against it rather than trusting
     * that `formStore`'s clear has already run: the mount effect and that clear are two
     * listeners on the same change, and nothing orders them.
     */
    listVersionId: string | null;
    /** The server's own words for a refused ladder, `null` unless `listStatus` is `failed`. */
    listError: string | null;
    /**
     * Sampled moments the last listing could not read, summed over every source and
     * counting one for each table whose ladder never landed at all. `0` on a clean
     * listing. The timeline still stands — every row in it is a true state — but it is
     * coarser than it should be, and a moment may be missing entirely, so the panel says
     * so and `retry` re-lists a `loaded` timeline only while this is above zero. A
     * refused rung anywhere coarsens the whole list, which is why this is a sum.
     */
    refusedMomentCount: number;
    /**
     * Where the timeline ends: the NARROWEST of the sources' ends, `null` until the
     * first listing lands. `'floor'` beats `'creation'` — if any source's history stops
     * at the retention floor, the merged history does too.
     */
    end: FormRestorePointsEnd | null;
    /**
     * The version ladder's own end and refused count, kept apart from the merged pair
     * above. A refinement splices rows into `points` and re-merges, and the merge needs
     * the source's values rather than the narrowed ones standing in for them.
     */
    versionEnd: FormRestorePointsEnd | null;
    versionRefusedMomentCount: number;
    /**
     * The later row of the gap being refined, `null` when no refinement is in flight.
     * One at a time: the pressed control renders `loading` from this and every other
     * control disables, so a person cannot spend thirty reads in three clicks. It is
     * one of the two tokens a refinement checks after its await, `listRequestId` being
     * the other — `clear` and `refresh` both return it to `null`, which retires a call
     * neither of them can cancel.
     */
    refiningLaterCursorMicros: number | null;
    /**
     * Later rows whose gap below is known to hold no further save, so their control
     * has nothing left to fetch. A refinement adds its later bound and every row it
     * returned, except — when it ended on `limit`, or reported a refused step — the
     * last, whose own gap is where the walk stopped rather than where it ran out.
     */
    exhaustedLaterCursors: number[];
    /**
     * The server's words for a refused refinement, against the later row it was asked
     * about. Held for one interval at a time because only one refinement runs at a
     * time; the control stays pressable, so this is a report and never a gate.
     */
    refineError: { laterCursorMicros: number; message: string } | null;
    /**
     * The row the user named, as its opaque `cursorMicros`, `null` before any row is
     * selected. Selecting a row invalidates `result`, so the two are always written
     * together; `useFormRecoveryRead` also uses it to tell its response from a later one.
     */
    selectedCursorMicros: number | null;
    readStatus: FormRecoveryStatus;
    /**
     * Raised by `useFormRecoveryRead` before every fan-out it sends, and what tells one
     * read's answers from another's. `selectedCursorMicros` cannot: selecting row A,
     * then B, then A again leaves the store naming exactly what read A asked for, so
     * read A's first response would satisfy read A's third guard, write its stale
     * result and flip the status — dropping the read the operator is waiting on, and
     * with it the digests a restore of that moment echoes back.
     *
     * Monotonic, and never reset — a clear that restarted the count would let a
     * response in flight match a later request. Every table read of one moment carries
     * the same value, because they are one fan-out.
     */
    readRequestId: number;
    /**
     * The last read response, `null` whenever `readStatus` is not `read`. The tint and
     * both surfaces render from it, so it is the whole response rather than a
     * projection — the group names fields from `diff`, the banner counts them.
     */
    result: ReadFormVersionAtResponse | null;
    /** The server's own words for a refused read, `null` unless `readStatus` is `refused`. */
    refusal: string | null;
    /**
     * One entry per table the selected moment names, written by `useFormRecoveryRead`
     * as each call lands and empty whenever nothing is selected. A table's refusal is
     * recorded on its own entry and takes nothing else down with it: the version's read
     * stands, and so does every other table's.
     */
    tableReads: RecoveryTableRead[];
    /**
     * The reachable window as the last successful response reported it. Held apart
     * from `result` because it outlives one: a refusal clears `result` and the window
     * notice must still be able to state the floor. Whenever `result` is non-null this
     * equals `result.window`.
     */
    window: FormPitrWindow | null;
    /**
     * Live `meta.created`, which the window notice names when no row on screen is the
     * document's creation.
     */
    liveCreatedAtMillis: number | null;
    /**
     * The form page is showing the selected row: `FormUI` is `forceDisabled` and every
     * differing field is tinted. Written only by `enterPreview`, `exitPreview` and the
     * two invalidating paths below; read by `FormPageContent`, the banner and the tint hook.
     */
    previewing: boolean;
    /** A restore is in flight — canvas state F. */
    restoring: boolean;
    /**
     * Raised by `useFormRecoveryRestore` before every restore it sends, on the model of
     * `listRequestId`. `restoring` alone cannot tell one restore from another: a stale
     * call landing after the operator started a second one would lower the shared token
     * and retire the live restore, which would then commit with no banner, no
     * `restoredFrom` and no undo. Monotonic, and never reset, for the same reason.
     */
    restoreRequestId: number;
    /** The server's own words for a refused restore, `null` until one is refused. */
    restoreError: string | null;
    /**
     * The restore that landed, held for the life of the route or until the banner is
     * dismissed — canvas state G. Outlives `select(null)` on purpose: the banner
     * carries the undo, which has to survive the selection being dropped, and the
     * per-table record of what the restore wrote and what it left behind, which is
     * the only place that account exists once `tableReads` is emptied.
     */
    restoredFrom: FormRecoveryRestoredFrom | null;
};

const state = proxy<FormRecoveryState>({
    points: [],
    tableIds: [],
    tableLists: [],
    tableListErrors: [],
    moments: [],
    listStatus: 'idle',
    listRequestId: 0,
    listVersionId: null,
    listError: null,
    refusedMomentCount: 0,
    end: null,
    versionEnd: null,
    versionRefusedMomentCount: 0,
    refiningLaterCursorMicros: null,
    exhaustedLaterCursors: [],
    refineError: null,
    selectedCursorMicros: null,
    readStatus: 'idle',
    readRequestId: 0,
    result: null,
    refusal: null,
    tableReads: [],
    window: null,
    liveCreatedAtMillis: null,
    previewing: false,
    restoring: false,
    restoreRequestId: 0,
    restoreError: null,
    restoredFrom: null,
});

/**
 * Names the row the user wants, discarding whatever was read before it. The preview
 * ends with it: a tint drawn from a result that no longer describes the named row
 * would mark the wrong fields. Moving `selectedCursorMicros` is also what retires an
 * in-flight read — `useFormRecoveryRead` compares against it. The loaded list is
 * untouched, because selecting a row says nothing about the ladder.
 *
 * `restoreError` goes with the result. It is the refusal of a restore attempted on
 * one row, and the row renders it, so carrying it to the next selection would tell
 * the operator a restore failed on a row nothing was ever attempted on.
 *
 * `tableReads` goes with it too, and for a harder reason than tidiness: each entry
 * carries the digests a restore of that table echoes back. Emptying the list here is
 * what makes it impossible for a digest read at one moment to be confirmed against
 * another.
 */
const select = (cursorMicros: number | null): void => {
    state.selectedCursorMicros = cursorMicros;
    state.readStatus = 'idle';
    state.result = null;
    state.refusal = null;
    state.tableReads = [];
    state.previewing = false;
    state.restoreError = null;
};

/**
 * Puts the page into the selected row. `flushAll` first, because `useFieldUI`
 * cancels its 600ms debounce the instant `disabled` flips true: an unflushed
 * keystroke would be discarded rather than saved. The flush runs every field's
 * `debouncedSave.flush()` synchronously, so the write is already in flight by the
 * time the disable reaches the fields.
 */
const enterPreview = (): void => {
    pendingSavesActions.flushAll();
    state.previewing = true;
};

const exitPreview = (): void => {
    state.previewing = false;
};

/**
 * Drops everything, the loaded ladder included. Called by `formStore` on `reset` and
 * on every form/version change, beside `formUndoStack.clear()`: rows are addresses
 * into one document's history, so none of them means anything against another
 * version. Returning both statuses to `idle` also retires any call still in flight.
 *
 * The three request counters survive it, deliberately: restarting the count would let a
 * response still in flight match a request sent after the clear.
 */
const clear = (): void => {
    state.points = [];
    state.tableIds = [];
    state.tableLists = [];
    state.tableListErrors = [];
    state.moments = [];
    state.listStatus = 'idle';
    state.listVersionId = null;
    state.listError = null;
    state.refusedMomentCount = 0;
    state.end = null;
    state.versionEnd = null;
    state.versionRefusedMomentCount = 0;
    state.refiningLaterCursorMicros = null;
    state.exhaustedLaterCursors = [];
    state.refineError = null;
    state.selectedCursorMicros = null;
    state.readStatus = 'idle';
    state.result = null;
    state.refusal = null;
    state.tableReads = [];
    state.window = null;
    state.liveCreatedAtMillis = null;
    state.previewing = false;
    state.restoring = false;
    state.restoreError = null;
    state.restoredFrom = null;
};

export const formRecoveryStore = Object.freeze({
    state,
    select,
    enterPreview,
    exitPreview,
    clear,
});
